import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useMemo } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { BillingInvoice } from '~hooks/useBilling/models';
import { makeCurrencyFormatter } from '~hooks/useCompanyCurrency/useCompanyCurrency';

type InvoiceCardVariant = 'open' | 'history';

type InvoiceAccent = 'open' | 'overdue' | 'paid';

const accentColor = (theme: Theme): Record<InvoiceAccent, string> => ({
  open: theme.semantic.statusInfo,
  overdue: theme.semantic.statusError,
  paid: theme.semantic.statusSuccess,
});

const deriveAccent = (invoice: BillingInvoice, variant: InvoiceCardVariant) => {
  if (variant === 'history') {
    return 'paid';
  }

  const isOverdue = Boolean(invoice.dueDate?.isBefore(dayjs(), 'day'));

  return isOverdue ? 'overdue' : 'open';
};

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

interface InvoiceCardProps {
  invoice: BillingInvoice;
  variant: InvoiceCardVariant;
}

export default function InvoiceCard({ invoice, variant }: InvoiceCardProps) {
  const accent = deriveAccent(invoice, variant);
  const formatCurrency = useMemo(
    () => makeCurrencyFormatter(invoice.currency),
    [invoice.currency],
  );
  const amount = variant === 'open' ? invoice.amountDue : invoice.amountPaid;

  return (
    <Box
      data-test-id={`settings-billing-invoice-card-${invoice.id}`}
      sx={(theme) => ({
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.semantic.surfaceOverlay
            : theme.semantic.surfaceRaised,
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: theme.spacing(),
        boxShadow:
          theme.palette.mode === 'dark'
            ? 'inset 0 1px 0 0 rgba(255, 255, 255, 0.06), 0 2px 8px 0 rgba(0, 0, 0, 0.55)'
            : '0px 1px 3px 0px rgba(0, 0, 0, 0.20)',
        display: 'flex',
        overflow: 'hidden',
        width: '100%',
      })}
    >
      <Box
        sx={(theme) => ({
          bgcolor: accentColor(theme)[accent],
          flex: 0,
          minWidth: '4px',
        })}
      />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          flexWrap: 'wrap',
          gap: 2,
          minWidth: 0,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ flex: 2, minWidth: '190px' }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {invoice.number ?? t('billing.tab.invoice')}
            </Typography>
            {accent === 'overdue' && (
              <Chip
                label={t('billing.tab.overdue')}
                size="small"
                sx={(theme) => ({
                  bgcolor: theme.semantic.statusErrorBg,
                  color: theme.semantic.statusError,
                  fontWeight: 600,
                })}
              />
            )}
            {accent === 'paid' && (
              <Chip
                label={t('status.paid')}
                size="small"
                sx={(theme) => ({
                  bgcolor: theme.semantic.statusSuccessBg,
                  color: theme.semantic.statusSuccess,
                  fontWeight: 600,
                })}
              />
            )}
          </Box>
          {invoice.lineDescriptions.length > 0 && (
            <Typography
              sx={(theme) => ({
                color: theme.semantic.textSecondary,
                fontSize: 12,
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              {invoice.lineDescriptions.join(' · ')}
            </Typography>
          )}
        </Box>
        {invoice.dueDate && (
          <Box sx={{ minWidth: '110px' }}>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray4,
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              })}
            >
              {t('billing.tab.due')}
            </Typography>
            <Typography
              sx={(theme) => ({
                color:
                  accent === 'overdue'
                    ? theme.semantic.statusError
                    : theme.semantic.textPrimary,
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: accent === 'overdue' ? 600 : 400,
              })}
            >
              {/* due_date is a calendar date (no time); a dayjs.tz() conversion can
                  shift it to the previous day and disagree with the overdue chip */}
              {invoice.dueDate.format('MMM D, YYYY')}
            </Typography>
          </Box>
        )}
        <Box sx={{ minWidth: '100px', textAlign: 'right' }}>
          <Typography
            sx={{ fontSize: 16, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
          >
            {formatCurrency(amount)}
          </Typography>
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray4,
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            })}
          >
            {invoice.currency}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {variant === 'open' && invoice.hostedInvoiceUrl && (
            <SmallButton
              color="brandV2Yellow"
              data-ui={`page-settings-billing__invoice__${invoice.id}__pay-button`}
              onClick={() => openInNewTab(invoice.hostedInvoiceUrl ?? '')}
            >
              {t('actions.pay')}
            </SmallButton>
          )}
          {variant === 'history' && invoice.hostedInvoiceUrl && (
            <SmallButton
              color="brandV2Black"
              data-ui={`page-settings-billing__invoice__${invoice.id}__view-button`}
              onClick={() => openInNewTab(invoice.hostedInvoiceUrl ?? '')}
              variant="outlined"
              sx={{ borderColor: 'divider', color: 'text.primary' }}
            >
              {t('actions.view')}
            </SmallButton>
          )}
          {variant === 'history' && invoice.invoicePdf && (
            <SmallButton
              color="brandV2Black"
              data-ui={`page-settings-billing__invoice__${invoice.id}__pdf-button`}
              onClick={() => openInNewTab(invoice.invoicePdf ?? '')}
              variant="outlined"
              sx={{ borderColor: 'divider', color: 'text.primary' }}
            >
              {t('actions.pdf')}
            </SmallButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}
